import React, { useState } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import clientConfig from '../config/clientConfig';

interface MapProps {
  center: google.maps.LatLngLiteral;
  zoom: number;
  address: string;
}

const MapComponent: React.FC<MapProps> = ({ center, zoom, address }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();

  React.useEffect(() => {
    if (ref.current && !map) {
      const newMap = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      // Add marker for the salon
      new window.google.maps.Marker({
        position: center,
        map: newMap,
        title: clientConfig.name,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
              <circle cx="20" cy="20" r="18" fill="#e91e63" stroke="#fff" stroke-width="2"/>
              <text x="20" y="26" text-anchor="middle" fill="white" font-size="20">💇</text>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20)
        }
      });

      // Add info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 10px; font-family: 'Roboto', sans-serif;">
            <h3 style="margin: 0 0 8px 0; color: #333;">${clientConfig.name}</h3>
            <p style="margin: 0; color: #666; font-size: 14px;">${address}</p>
          </div>
        `
      });

      const marker = new window.google.maps.Marker({
        position: center,
        map: newMap,
        title: clientConfig.name
      });

      marker.addListener('click', () => {
        infoWindow.open(newMap, marker);
      });

      setMap(newMap);
    }
  }, [ref, map, center, zoom, address]);

  return <div ref={ref} style={{ width: '100%', height: '100%' }} />;
};

const render = (status: Status) => {
  switch (status) {
    case Status.LOADING:
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    case Status.FAILURE:
      return (
        <Alert severity="error" sx={{ height: 400, display: 'flex', alignItems: 'center' }}>
          Erreur lors du chargement de Google Maps
        </Alert>
      );
    case Status.SUCCESS:
      return <MapComponent center={clientConfig.location} zoom={15} address={clientConfig.address.full} />;
  }
};

interface GoogleMapProps {
  height?: number | string;
  showAddress?: boolean;
}

const GoogleMap: React.FC<GoogleMapProps> = ({ height = 400, showAddress = true }) => {
  
  return (
    <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      {showAddress && (
        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', color: '#495057', fontWeight: 600 }}>
            <LocationIcon sx={{ mr: 1, color: '#e91e63' }} />
            {clientConfig.name}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6c757d', mt: 0.5 }}>
            {clientConfig.address.full}
          </Typography>
        </Box>
      )}
      <Box sx={{ height }}>
        <Wrapper
          apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
          render={render}
          libraries={['places']}
        />
      </Box>
    </Paper>
  );
};

export default GoogleMap;
